"use client";

import Image from "next/image";
import Link from "next/link";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { cardMatchesQuery } from "@/lib/card-search";
import { DOMAIN_COLORS, baseLegendKey } from "@/lib/domains";
import { deleteDeck, hydrateDeckEntries, listDecks, listInventory, saveDeck, type InventoryEntry, type SavedDeck } from "@/lib/local-store";
import { migrateImportedPrepDecksToSaved } from "@/lib/deck-import";
import type { CardRecord, DeckEntry } from "@/lib/types";
import DeckImportForm from "@/components/DeckImportForm";
import MissingCardsPanel from "@/components/MissingCardsPanel";

type BuildStep = "legend" | "champion" | "cards";
type CardTab = "Unit" | "Spell" | "Gear" | "Battlefield";
type DropZoneType = "Unit" | "Gear" | "Spell" | "Battlefield" | "Sideboard";
type ChampionQty = 1 | 2 | 3;
type RightPanel = "main" | "sideboard";

const TARGET_DECK_SIZE = 40;
const TARGET_BATTLEFIELDS = 3;
const PAGE_SIZE = 10;
const ZONE_PAGE_SIZE = 6;
const SIDEBOARD_PAGE_SIZE = 10;
const CARD_DND_MIME = "application/x-riftbound-card";
const ZONE_CARD_SLOTS = 6; // taille visuelle commune (référence Units)

const DROP_ZONES: Array<{
  type: Exclude<DropZoneType, "Sideboard">;
  label: string;
  accent: string;
  ring: string;
}> = [
  { type: "Unit", label: "Units", accent: "border-blue-500/30 bg-blue-500/5", ring: "ring-blue-400/50" },
  { type: "Gear", label: "Gears", accent: "border-amber-500/30 bg-amber-500/5", ring: "ring-amber-400/50" },
  { type: "Spell", label: "Spells", accent: "border-purple-500/30 bg-purple-500/5", ring: "ring-purple-400/50" },
  { type: "Battlefield", label: "Battlefields", accent: "border-cyan-500/30 bg-cyan-500/5", ring: "ring-cyan-400/50" },
];

type ZoneCardItem = {
  key: string;
  card: CardRecord;
  quantity: number;
  locked?: boolean;
};

function upsert(entries: DeckEntry[], card: CardRecord, delta: number): DeckEntry[] {
  const existing = entries.find((e) => e.card.id === card.id);
  if (!existing && delta <= 0) return entries;
  if (!existing) return [...entries, { card, quantity: delta }];
  const next = Math.max(0, existing.quantity + delta);
  if (next === 0) return entries.filter((e) => e.card.id !== card.id);
  return entries.map((e) => (e.card.id === card.id ? { ...e, quantity: next } : e));
}

/** "Annie - Dark Child (Starter)" / "Akali, Deadly Weapon" / "Yordle, Kennen - …" → "annie" / "akali" / "kennen" */
function championIdentity(name: string): string {
  const stripped = name.replace(/\s*\([^)]*\)\s*$/u, "").trim();
  const beforeDash = stripped.split(" - ")[0]?.trim() || stripped;
  const racePrefix = new Set(["yordle", "darkin", "vastayan", "ascended", "voidborn"]);

  if (beforeDash.includes(",")) {
    const parts = beforeDash
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2 && racePrefix.has(parts[0].toLowerCase())) {
      return parts[1].toLowerCase();
    }
    return parts[0].toLowerCase();
  }

  return beforeDash.toLowerCase();
}

function DomainPills({ domains }: { domains: string[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {domains.map((d) => (
        <span
          key={d}
          className="rounded-full px-2 py-0.5 text-[10px] font-bold border"
          style={{
            color: DOMAIN_COLORS[d]?.text,
            background: DOMAIN_COLORS[d]?.bg,
            borderColor: DOMAIN_COLORS[d]?.border,
          }}
        >
          {d}
        </span>
      ))}
    </div>
  );
}

function PaginatedZoneStrip({
  items,
  pageSize,
  onAdd,
  onRemove,
  emptyLabel,
  fillSlots = false,
  growToFill = false,
  gridCols,
}: {
  items: ZoneCardItem[];
  pageSize: number;
  onAdd?: (card: CardRecord) => void;
  onRemove: (card: CardRecord) => void;
  emptyLabel: string;
  /** Cases fixes qui partagent toute la zone (Battlefields) */
  fillSlots?: boolean;
  /** Cartes qui s'étirent selon le nb d'uniques jusqu'à pageSize (Gears / Spells) */
  growToFill?: boolean;
  /** Grille multi-lignes (ex. sideboard 5 cols × 2 rows) */
  gridCols?: number;
}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const stretch = fillSlots || growToFill;
  const useGrid = Boolean(gridCols);

  useEffect(() => {
    setPage(0);
  }, [items.length, pageSize]);

  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  const visible = items.slice(page * pageSize, page * pageSize + pageSize);
  const displayItems: (ZoneCardItem | null)[] = fillSlots
    ? Array.from({ length: pageSize }, (_, i) => visible[i] ?? null)
    : visible;

  const cardShell = (item: ZoneCardItem) => (
    <div
      key={item.key}
      className={`relative min-h-0 min-w-0 ${
        useGrid ? "h-full w-full" : stretch ? "h-full flex-1" : "shrink-0"
      }`}
      style={
        useGrid || stretch
          ? { maxHeight: "100%" }
          : {
              height: "90%",
              maxWidth: "var(--zone-card-w)",
              aspectRatio: "5 / 7",
            }
      }
    >
      {item.card.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={item.card.imageUrl}
          alt={item.card.name}
          title={item.card.name}
          className="h-full w-full rounded-md object-contain"
          draggable={false}
        />
      ) : (
        <div className="flex h-full items-center justify-center rounded-md bg-black/40 text-[10px]">
          {item.card.name}
        </div>
      )}
      {!item.locked && (
        <div className="absolute bottom-0.5 left-1/2 flex -translate-x-1/2 items-center gap-0.5 rounded bg-black/75 px-0.5 py-0.5">
          <button
            type="button"
            onClick={() => onRemove(item.card)}
            className="h-5 w-5 rounded bg-white/10 text-[11px] hover:bg-white/20"
          >
            −
          </button>
          {onAdd && (
            <>
              <span className="min-w-[1rem] text-center text-[10px] font-bold tabular-nums">
                {item.quantity}
              </span>
              <button
                type="button"
                onClick={() => onAdd(item.card)}
                className="h-5 w-5 rounded bg-white/10 text-[11px] hover:bg-white/20"
              >
                +
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {items.length === 0 ? (
        <p className="flex min-h-0 flex-1 items-center justify-center text-[11px] text-gray-500">
          {emptyLabel}
        </p>
      ) : useGrid ? (
        <div
          className="grid min-h-0 flex-1 gap-1.5 overflow-hidden px-0.5 py-1"
          style={{
            gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${Math.ceil(pageSize / (gridCols ?? 1))}, minmax(0, 1fr))`,
          }}
        >
          {displayItems.map((item) => (item ? cardShell(item) : null))}
        </div>
      ) : (
        <div
          className={`flex min-h-0 flex-1 items-center overflow-y-hidden px-0.5 py-1 ${
            stretch ? "justify-center gap-2" : "justify-start gap-1 overflow-x-auto"
          }`}
        >
          {displayItems.map((item, idx) =>
            item ? (
              cardShell(item)
            ) : fillSlots ? (
              <div
                key={`empty-${idx}`}
                className="flex h-full min-w-0 flex-1 items-center justify-center rounded-md border border-dashed border-white/10 bg-black/20 text-[10px] text-gray-600"
              />
            ) : null,
          )}
        </div>
      )}
      {items.length > pageSize && (
        <div className="mt-1 flex shrink-0 items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-sm disabled:opacity-30"
          >
            ‹
          </button>
          <span className="text-[10px] tabular-nums text-gray-400">
            {page + 1}/{totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-sm disabled:opacity-30"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

function PaginatedCardGrid({
  cards,
  selectedId,
  onSelect,
  selectedClassName,
  showDomains,
}: {
  cards: CardRecord[];
  selectedId: string | null;
  onSelect: (card: CardRecord) => void;
  selectedClassName: string;
  showDomains?: boolean;
}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(cards.length / PAGE_SIZE));

  useEffect(() => {
    setPage(0);
  }, [cards]);

  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  const visible = cards.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {visible.map((card) => {
          const selected = card.id === selectedId;
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => onSelect(card)}
              className={`group rounded-2xl border p-2 text-left transition ${
                selected
                  ? selectedClassName
                  : "border-white/5 bg-white/[0.03] hover:border-white/20"
              }`}
            >
              {card.imageUrl ? (
                <Image
                  src={card.imageUrl}
                  alt={card.name}
                  width={200}
                  height={280}
                  unoptimized
                  className="w-full rounded-xl"
                />
              ) : (
                <div className="flex aspect-[5/7] items-center justify-center rounded-xl bg-black/40 text-gray-500">
                  ?
                </div>
              )}
              <div className="mt-2 space-y-1 px-1">
                <p className="truncate text-sm font-semibold">{card.name}</p>
                {showDomains ? <DomainPills domains={card.domains} /> : null}
              </div>
            </button>
          );
        })}
      </div>

      {cards.length > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-xl font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Page précédente"
          >
            ‹
          </button>
          <span className="min-w-[5rem] text-center text-sm font-medium tabular-nums text-gray-400">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-xl font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Page suivante"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

export default function DeckBuilder() {
  const [cards, setCards] = useState<CardRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState<SavedDeck[]>([]);
  const [inventory, setInventory] = useState<InventoryEntry[]>([]);
  const [gapsDeckId, setGapsDeckId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [view, setView] = useState<"list" | "builder">("list");

  const [step, setStep] = useState<BuildStep>("legend");
  const [selectedLegendId, setSelectedLegendId] = useState<string | null>(null);
  const [selectedChampionId, setSelectedChampionId] = useState<string | null>(null);
  const [deckEntries, setDeckEntries] = useState<DeckEntry[]>([]);
  const [battlefieldIds, setBattlefieldIds] = useState<string[]>([]);
  const [sideboardEntries, setSideboardEntries] = useState<DeckEntry[]>([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<CardTab>("Unit");
  const [deckName, setDeckName] = useState("");
  const [championQty, setChampionQty] = useState<ChampionQty>(3);
  const [dragOverZone, setDragOverZone] = useState<DropZoneType | null>(null);
  const [poolPage, setPoolPage] = useState(0);
  const [rightPanel, setRightPanel] = useState<RightPanel>("main");
  const [showImport, setShowImport] = useState(false);

  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    fetch("/api/cards")
      .then((r) => r.json())
      .then((data: { cards: CardRecord[] }) => {
        const next = data.cards ?? [];
        setCards(next);
        if (migrateImportedPrepDecksToSaved(next) > 0) setSaved(listDecks());
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
    setSaved(listDecks());
    setInventory(listInventory());
    const onSync = () => {
      setSaved(listDecks());
      setInventory(listInventory());
    };
    window.addEventListener("rifthub:storage-sync", onSync);
    return () => window.removeEventListener("rifthub:storage-sync", onSync);
  }, []);

  const legends = useMemo(
    () =>
      cards
        .filter((c) => c.type === "Legend")
        // Ignore broken labels like "Defender of Tomorrow" (missing champion prefix)
        .filter((c) => /[-,]/.test(c.name))
        .reduce<CardRecord[]>((acc, card) => {
          const key = baseLegendKey(card.name);
          if (!acc.some((c) => baseLegendKey(c.name) === key)) acc.push(card);
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name)),
    [cards],
  );

  const selectedLegend = useMemo(
    () => cards.find((c) => c.id === selectedLegendId) ?? null,
    [cards, selectedLegendId],
  );

  const legendDomains = useMemo(
    () => new Set(selectedLegend?.domains ?? []),
    [selectedLegend],
  );

  const championsForLegend = useMemo(() => {
    if (!selectedLegend) return [];
    const legendId = championIdentity(selectedLegend.name);
    return cards
      .filter((c) => {
        if (c.type !== "Unit" || c.supertype !== "Champion") return false;
        const sameChampion =
          championIdentity(c.name) === legendId ||
          c.tags.some((tag) => tag.trim().toLowerCase() === legendId);
        if (!sameChampion) return false;
        // Chosen champion doit être dans les couleurs de la légende
        return c.domains.some((d) => legendDomains.has(d));
      })
      .reduce<CardRecord[]>((acc, card) => {
        const key = baseLegendKey(card.name).toLowerCase();
        if (!acc.some((c) => baseLegendKey(c.name).toLowerCase() === key)) acc.push(card);
        return acc;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cards, selectedLegend, legendDomains]);

  const filteredCards = useMemo(() => {
    return cards.filter((c) => {
      if (c.type !== tab) return false;
      // Le chosen champion se gère à l'étape précédente
      if (c.id === selectedChampionId) return false;
      if (tab !== "Battlefield") {
        const matchDomain =
          c.domains.some((d) => legendDomains.has(d)) || c.domains.includes("Colorless");
        if (!matchDomain) return false;
      }
      return cardMatchesQuery(c, deferredSearch);
    });
  }, [cards, deferredSearch, tab, legendDomains, selectedChampionId]);

  const poolPages = Math.max(1, Math.ceil(filteredCards.length / PAGE_SIZE));
  const poolVisible = filteredCards.slice(poolPage * PAGE_SIZE, poolPage * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => {
    setPoolPage(0);
  }, [tab, deferredSearch, selectedLegendId]);

  useEffect(() => {
    if (poolPage > poolPages - 1) setPoolPage(Math.max(0, poolPages - 1));
  }, [poolPage, poolPages]);

  const mainEntries = useMemo(
    () => deckEntries.filter((e) => e.card.type !== "Battlefield" && e.card.type !== "Rune"),
    [deckEntries],
  );

  const entriesByType = useMemo(() => {
    const groups: Record<"Unit" | "Gear" | "Spell", DeckEntry[]> = {
      Unit: [],
      Gear: [],
      Spell: [],
    };
    for (const entry of mainEntries) {
      if (entry.card.type === "Unit" || entry.card.type === "Gear" || entry.card.type === "Spell") {
        groups[entry.card.type].push(entry);
      }
    }
    for (const key of Object.keys(groups) as Array<keyof typeof groups>) {
      groups[key].sort((a, b) => (a.card.energy ?? 99) - (b.card.energy ?? 99) || a.card.name.localeCompare(b.card.name));
    }
    return groups;
  }, [mainEntries]);

  const battlefieldCards = useMemo(
    () =>
      battlefieldIds
        .map((id) => cards.find((c) => c.id === id))
        .filter((c): c is CardRecord => Boolean(c)),
    [battlefieldIds, cards],
  );

  const sideboardCount = useMemo(
    () => sideboardEntries.reduce((s, e) => s + e.quantity, 0),
    [sideboardEntries],
  );

  const sortedSideboard = useMemo(
    () =>
      [...sideboardEntries].sort(
        (a, b) =>
          (a.card.energy ?? 99) - (b.card.energy ?? 99) || a.card.name.localeCompare(b.card.name),
      ),
    [sideboardEntries],
  );

  const mainCount = useMemo(
    () => mainEntries.reduce((s, e) => s + e.quantity, 0),
    [mainEntries],
  );

  const runeEntries = useMemo(() => {
    if (!selectedLegend) return [];
    return selectedLegend.domains.flatMap((domain) => {
      const rune = cards.find(
        (c) => c.type === "Rune" && c.domains.includes(domain) && !c.domains.includes("Colorless"),
      );
      if (!rune) return [];
      return [{ cardId: rune.id, quantity: 6 }];
    });
  }, [cards, selectedLegend]);

  const selectedChampion = useMemo(
    () => cards.find((c) => c.id === selectedChampionId) ?? null,
    [cards, selectedChampionId],
  );

  function resetBuilder() {
    setEditingId(null);
    setStep("legend");
    setSelectedLegendId(null);
    setSelectedChampionId(null);
    setChampionQty(3);
    setDeckEntries([]);
    setBattlefieldIds([]);
    setSideboardEntries([]);
    setSearch("");
    setDeckName("");
    setTab("Unit");
    setDragOverZone(null);
    setPoolPage(0);
    setRightPanel("main");
  }

  function startFresh() {
    resetBuilder();
    setView("builder");
  }

  function openDeck(deck: SavedDeck) {
    setEditingId(deck.id);
    setDeckName(deck.name);
    setSelectedLegendId(deck.legendId);
    setSelectedChampionId(deck.championId);
    const hydrated = hydrateDeckEntries(deck, cards).filter((e) => e.card.type !== "Rune");
    setDeckEntries(hydrated);
    const champEntry = hydrated.find((e) => e.card.id === deck.championId);
    const qty = champEntry?.quantity ?? 3;
    setChampionQty(qty >= 3 ? 3 : qty <= 1 ? 1 : 2);
    setBattlefieldIds(deck.battlefieldIds ?? []);
    const sideRaw = deck.sideboard ?? [];
    const byId = new Map(cards.map((c) => [c.id, c]));
    setSideboardEntries(
      sideRaw.flatMap((e) => {
        const card = byId.get(e.cardId);
        if (!card || e.quantity <= 0) return [];
        return [{ card, quantity: e.quantity }];
      }),
    );
    setRightPanel("main");
    setStep("cards");
    setView("builder");
  }

  function applyChampion(card: CardRecord, qty: ChampionQty) {
    setSelectedChampionId(card.id);
    setChampionQty(qty);
    setDeckEntries((entries) => {
      const withoutOldChamp = entries.filter(
        (e) => e.card.id !== card.id && e.card.id !== selectedChampionId,
      );
      return [...withoutOldChamp, { card, quantity: qty }];
    });
  }

  function selectChampion(card: CardRecord) {
    applyChampion(card, championQty);
  }

  function changeChampionQty(qty: ChampionQty) {
    setChampionQty(qty);
    if (selectedChampion) applyChampion(selectedChampion, qty);
  }

  function toggleBattlefield(card: CardRecord) {
    setBattlefieldIds((ids) => {
      if (ids.includes(card.id)) return ids.filter((id) => id !== card.id);
      if (ids.length >= TARGET_BATTLEFIELDS) return ids;
      return [...ids, card.id];
    });
  }

  function addToMain(card: CardRecord) {
    if (card.type === "Battlefield") {
      toggleBattlefield(card);
      return;
    }
    if (card.id === selectedChampionId) return;
    if (mainCount >= TARGET_DECK_SIZE) return;
    setDeckEntries((e) => upsert(e, card, 1));
  }

  function removeFromMain(card: CardRecord) {
    if (card.type === "Battlefield") {
      setBattlefieldIds((ids) => ids.filter((id) => id !== card.id));
      return;
    }
    if (card.id === selectedChampionId) return;
    setDeckEntries((e) => upsert(e, card, -1));
  }

  function addCard(card: CardRecord) {
    if (rightPanel === "sideboard") {
      addToSideboard(card);
      return;
    }
    addToMain(card);
  }

  function removeFromSideboard(card: CardRecord) {
    setSideboardEntries((e) => upsert(e, card, -1));
  }

  function addToSideboard(card: CardRecord) {
    setSideboardEntries((e) => upsert(e, card, 1));
  }

  function dropCardIntoZone(cardId: string, zone: DropZoneType) {
    const card = cards.find((c) => c.id === cardId);
    if (!card) return;
    if (zone === "Sideboard") {
      addToSideboard(card);
      return;
    }
    if (card.type !== zone) return;
    if (card.id === selectedChampionId) return;
    addToMain(card);
  }

  function handleSave() {
    if (!selectedLegend) return;
    const name = deckName.trim() || window.prompt("Nom du deck", `Deck ${selectedLegend.name}`);
    if (!name) return;
    const deck = saveDeck({
      id: editingId ?? undefined,
      name,
      legend: selectedLegend,
      championId: selectedChampionId,
      entries: mainEntries,
      battlefieldIds,
      sideboard: sideboardEntries,
      runeEntries,
    });
    setSaved(listDecks());
    setEditingId(deck.id);
    setDeckName(deck.name);
    window.alert(
      `Deck sauvegardé · ${deck.totalCards} cartes (+ runes) · sideboard ${sideboardCount}`,
    );
  }

  function handleDelete(id: string) {
    if (!window.confirm("Supprimer ce deck ?")) return;
    deleteDeck(id);
    setSaved(listDecks());
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center gap-3 text-gray-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        Chargement des cartes…
      </div>
    );
  }

  if (view === "list") {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-400/80">Decks</p>
            <h1 className="mt-1 text-4xl font-black">
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Tes listes
              </span>
            </h1>
            <p className="mt-2 text-gray-500">
              Construit pour le sideboard · croise avec l&apos;inventaire pour les achats.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowImport((v) => !v)}
              className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-bold text-white hover:bg-white/10"
            >
              Importer
            </button>
            <button
              type="button"
              onClick={startFresh}
              className="rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-bold shadow-lg shadow-blue-500/20 transition hover:scale-105"
            >
              Nouveau deck
            </button>
          </div>
        </div>

        {showImport && (
          <div className="mb-6">
            <DeckImportForm
              cards={cards}
              onCancel={() => setShowImport(false)}
              onImported={(deck) => {
                setSaved(listDecks());
                setShowImport(false);
                openDeck(deck);
              }}
            />
          </div>
        )}

        {saved.length === 0 ? (
          <div className="glass rounded-2xl border border-white/5 p-12 text-center text-gray-400">
            Aucun deck pour l&apos;instant. Crée ta première liste.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {saved.map((deck) => (
              <div key={deck.id} className="glass card-hover rounded-2xl border border-white/5 p-5">
                <h3 className="text-lg font-bold">{deck.name}</h3>
                <p className="mt-1 text-sm text-gray-400">{deck.legendName}</p>
                <p className="mt-3 text-xs text-gray-500">
                  {deck.totalCards} cartes · {deck.battlefieldIds?.length ?? 0} BF ·{" "}
                  {(deck.sideboard ?? []).reduce((s, e) => s + e.quantity, 0)} SB
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => openDeck(deck)}
                    className="rounded-xl bg-white/10 px-3 py-1.5 text-sm font-medium hover:bg-white/15"
                  >
                    Éditer
                  </button>
                  <Link
                    href="/prepa"
                    className="rounded-xl bg-emerald-600/80 px-3 py-1.5 text-sm font-medium hover:bg-emerald-500"
                  >
                    Sideboard
                  </Link>
                  <button
                    type="button"
                    onClick={() => setGapsDeckId((id) => (id === deck.id ? null : deck.id))}
                    className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-sm font-medium text-amber-100 hover:bg-amber-500/20"
                  >
                    Manquantes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(deck.id)}
                    className="rounded-xl px-3 py-1.5 text-sm text-red-300 hover:bg-red-500/10"
                  >
                    Supprimer
                  </button>
                </div>
                {gapsDeckId === deck.id && (
                  <div className="mt-4">
                    <MissingCardsPanel deck={deck} inventory={inventory} cards={cards} compact />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const steps: BuildStep[] = ["legend", "champion", "cards"];

  if (step === "cards" && selectedLegend) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden bg-[#060611]">
        {/* Barre du haut : navigation + nom + champion + save */}
        <div className="shrink-0 border-b border-white/5 px-4 py-3 sm:px-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setView("list");
                resetBuilder();
              }}
              className="text-sm text-gray-400 hover:text-white"
            >
              ← Mes decks
            </button>
            <div className="flex gap-2">
              {steps.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStep(s)}
                  className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                    step === s
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                      : "bg-white/5 text-gray-400"
                  }`}
                >
                  {s === "legend" ? "1. Légende" : s === "champion" ? "2. Champion" : "3. Cartes"}
                </button>
              ))}
            </div>
          </div>

          <div className="glass flex flex-wrap items-center gap-3 rounded-2xl border border-white/5 p-3">
            <input
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              placeholder={`Deck ${selectedLegend.name}`}
              className="min-w-[180px] flex-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm font-semibold outline-none focus:border-blue-500/50"
            />

            <DomainPills domains={selectedLegend.domains} />

            {selectedChampion && (
              <div className="flex items-center gap-2 rounded-xl border border-purple-500/20 bg-purple-500/10 px-3 py-1.5">
                {selectedChampion.imageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={selectedChampion.imageUrl}
                    alt={selectedChampion.name}
                    className="h-9 w-7 rounded object-cover"
                  />
                ) : null}
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-purple-300">Chosen</p>
                  <p className="max-w-[140px] truncate text-xs font-semibold">{selectedChampion.name}</p>
                </div>
                <div className="flex gap-1">
                  {([1, 2, 3] as ChampionQty[]).map((qty) => (
                    <button
                      key={qty}
                      type="button"
                      onClick={() => changeChampionQty(qty)}
                      className={`rounded-lg px-2.5 py-1 text-xs font-bold ${
                        championQty === qty ? "bg-purple-600 text-white" : "bg-white/5 text-gray-400"
                      }`}
                    >
                      {qty}×
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <span
                className={`text-sm font-bold tabular-nums ${
                  mainCount === TARGET_DECK_SIZE ? "text-emerald-400" : "text-amber-300"
                }`}
              >
                {mainCount}/{TARGET_DECK_SIZE}
              </span>
              <div className="h-2 w-24 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                  style={{ width: `${Math.min(100, (mainCount / TARGET_DECK_SIZE) * 100)}%` }}
                />
              </div>
            </div>

            <div className="flex rounded-xl bg-white/5 p-1">
              <button
                type="button"
                onClick={() => setRightPanel("main")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                  rightPanel === "main" ? "bg-white/15 text-white" : "text-gray-400"
                }`}
              >
                Main
              </button>
              <button
                type="button"
                onClick={() => setRightPanel("sideboard")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                  rightPanel === "sideboard" ? "bg-emerald-600 text-white" : "text-gray-400"
                }`}
              >
                Sideboard ({sideboardCount})
              </button>
            </div>

            <button
              type="button"
              onClick={() => setStep("champion")}
              className="rounded-xl bg-white/5 px-3 py-2 text-sm text-gray-300 hover:bg-white/10"
            >
              ← Champion
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-2.5 text-sm font-bold shadow-lg shadow-blue-500/20"
            >
              Sauvegarder
            </button>
          </div>
        </div>

        {/* Corps : pool gauche / cases droite */}
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 p-3 lg:grid-cols-2 lg:gap-4 lg:p-4">
          {/* Gauche : pool */}
          <div className="glass flex min-h-0 flex-col rounded-2xl border border-white/5 p-3">
            <div className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom ou numéro (ex. OGN 195)…"
                className="min-w-[140px] flex-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-blue-500/50"
              />
              <div className="flex gap-1 rounded-xl bg-white/5 p-1">
                {(["Unit", "Gear", "Spell", "Battlefield"] as CardTab[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`rounded-lg px-2.5 py-1.5 text-[11px] font-bold ${
                      tab === t ? "bg-white/15 text-white" : "text-gray-400"
                    }`}
                  >
                    {t === "Unit" ? "Units" : t === "Gear" ? "Gears" : t === "Spell" ? "Spells" : "BF"}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 xl:grid-cols-5">
                {poolVisible.map((card) => {
                  const qty =
                    card.type === "Battlefield"
                      ? battlefieldIds.includes(card.id)
                        ? 1
                        : 0
                      : deckEntries.find((e) => e.card.id === card.id)?.quantity ?? 0;
                  return (
                    <div
                      key={card.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(CARD_DND_MIME, card.id);
                        e.dataTransfer.setData("text/plain", card.id);
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                      onDoubleClick={() => addCard(card)}
                      className={`cursor-grab rounded-xl border p-1.5 active:cursor-grabbing ${
                        qty > 0 ? "border-cyan-500/40 bg-cyan-500/5" : "border-white/5 bg-white/[0.03]"
                      }`}
                    >
                      {card.imageUrl ? (
                        <Image
                          src={card.imageUrl}
                          alt={card.name}
                          width={120}
                          height={168}
                          unoptimized
                          className="pointer-events-none w-full rounded-lg"
                          draggable={false}
                        />
                      ) : null}
                      <p className="mt-1 truncate text-[10px] font-semibold">{card.name}</p>
                      {qty > 0 && (
                        <p className="text-[9px] font-bold text-cyan-300">×{qty}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {filteredCards.length === 0 && (
                <p className="py-10 text-center text-sm text-gray-500">Aucune carte.</p>
              )}
            </div>

            {filteredCards.length > PAGE_SIZE && (
              <div className="mt-2 flex shrink-0 items-center justify-center gap-3 border-t border-white/5 pt-2">
                <button
                  type="button"
                  onClick={() => setPoolPage((p) => Math.max(0, p - 1))}
                  disabled={poolPage === 0}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-lg disabled:opacity-30"
                >
                  ‹
                </button>
                <span className="text-xs tabular-nums text-gray-400">
                  {poolPage + 1} / {poolPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPoolPage((p) => Math.min(poolPages - 1, p + 1))}
                  disabled={poolPage >= poolPages - 1}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-lg disabled:opacity-30"
                >
                  ›
                </button>
              </div>
            )}
          </div>

          {/* Droite : main zones OU sideboard */}
          {rightPanel === "sideboard" ? (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
                setDragOverZone("Sideboard");
              }}
              onDragLeave={() => setDragOverZone((z) => (z === "Sideboard" ? null : z))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverZone(null);
                const id =
                  e.dataTransfer.getData(CARD_DND_MIME) || e.dataTransfer.getData("text/plain");
                if (id) dropCardIntoZone(id, "Sideboard");
              }}
              className={`flex min-h-0 flex-col rounded-2xl border border-dashed border-emerald-500/30 bg-emerald-500/5 p-3 transition ${
                dragOverZone === "Sideboard" ? "ring-2 ring-emerald-400/50" : ""
              }`}
            >
              <div className="mb-2 flex shrink-0 items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-300">Sideboard</h3>
                <span className="text-xs font-bold tabular-nums text-gray-400">{sideboardCount} cartes</span>
              </div>
              <p className="mb-2 shrink-0 text-[11px] text-gray-500">
                N&apos;importe quelle carte · glisser depuis le pool ou double-clic
              </p>
              <PaginatedZoneStrip
                items={sortedSideboard.map((e) => ({
                  key: e.card.id,
                  card: e.card,
                  quantity: e.quantity,
                }))}
                pageSize={SIDEBOARD_PAGE_SIZE}
                gridCols={5}
                onAdd={addToSideboard}
                onRemove={removeFromSideboard}
                emptyLabel="Dépose des cartes de sideboard ici"
              />
            </div>
          ) : (
            <div
              className="grid min-h-0 gap-1.5 overflow-hidden"
              style={{
                gridTemplateRows: "2fr 1fr 1fr",
                gridTemplateColumns: "3fr 5fr",
                gridTemplateAreas: `
                  "units units"
                  "gears spells"
                  "bf bf"
                `,
                // Largeur carte = panel / 6 (réf. Units) → même taille partout
                ["--zone-card-w" as string]: `calc((100% - ${ZONE_CARD_SLOTS - 1} * 0.25rem) / ${ZONE_CARD_SLOTS})`,
              }}
            >
              {DROP_ZONES.map((zone) => {
                const items: ZoneCardItem[] =
                  zone.type === "Battlefield"
                    ? battlefieldCards.map((card) => ({
                        key: card.id,
                        card,
                        quantity: 1,
                      }))
                    : entriesByType[zone.type].map((entry) => ({
                        key: entry.card.id,
                        card: entry.card,
                        quantity: entry.quantity,
                        locked: entry.card.id === selectedChampionId,
                      }));
                const count =
                  zone.type === "Battlefield"
                    ? battlefieldCards.length
                    : entriesByType[zone.type].reduce((s, e) => s + e.quantity, 0);
                const limitLabel =
                  zone.type === "Battlefield" ? `${count}/${TARGET_BATTLEFIELDS}` : `${count}`;
                const isOver = dragOverZone === zone.type;
                const area =
                  zone.type === "Unit"
                    ? "units"
                    : zone.type === "Gear"
                      ? "gears"
                      : zone.type === "Spell"
                        ? "spells"
                        : "bf";
                return (
                  <div
                    key={zone.type}
                    style={{ gridArea: area }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "copy";
                      setDragOverZone(zone.type);
                    }}
                    onDragLeave={() => setDragOverZone((z) => (z === zone.type ? null : z))}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverZone(null);
                      const id =
                        e.dataTransfer.getData(CARD_DND_MIME) || e.dataTransfer.getData("text/plain");
                      if (id) dropCardIntoZone(id, zone.type);
                    }}
                    className={`flex min-h-0 flex-col rounded-2xl border border-dashed p-1.5 transition ${zone.accent} ${
                      isOver ? `ring-2 ${zone.ring}` : ""
                    }`}
                  >
                    <div className="mb-0.5 flex shrink-0 items-center justify-between px-0.5">
                      <h3 className="text-[11px] font-bold uppercase tracking-wide">{zone.label}</h3>
                      <span className="text-[11px] font-bold tabular-nums text-gray-400">{limitLabel}</span>
                    </div>
                    <PaginatedZoneStrip
                      items={items}
                      pageSize={
                        zone.type === "Unit"
                          ? 6
                          : zone.type === "Gear"
                            ? 4
                            : zone.type === "Spell"
                              ? 5
                              : 3
                      }
                      fillSlots={zone.type === "Battlefield"}
                      growToFill={zone.type === "Gear" || zone.type === "Spell"}
                      onAdd={zone.type === "Battlefield" ? undefined : addToMain}
                      onRemove={removeFromMain}
                      emptyLabel={`Dépose des ${zone.label.toLowerCase()} ici`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            setView("list");
            resetBuilder();
          }}
          className="text-sm text-gray-400 hover:text-white"
        >
          ← Mes decks
        </button>
        <div className="flex gap-2">
          {steps.map((s) => (
            <button
              key={s}
              type="button"
              disabled={
                (s === "champion" && !selectedLegendId) ||
                (s === "cards" && !selectedChampionId)
              }
              onClick={() => setStep(s)}
              className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                step === s
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                  : "bg-white/5 text-gray-400"
              }`}
            >
              {s === "legend" ? "1. Légende" : s === "champion" ? "2. Champion" : "3. Cartes"}
            </button>
          ))}
        </div>
      </div>

      {step === "legend" && (
        <section>
          <h2 className="mb-2 text-2xl font-bold">Choisis ta légende</h2>
          <p className="mb-6 text-sm text-gray-500">
            {legends.length} légendes · 10 par page · les domaines filtrent ensuite le pool de cartes.
          </p>
          <PaginatedCardGrid
            cards={legends}
            selectedId={selectedLegendId}
            showDomains
            selectedClassName="border-blue-500/60 bg-blue-500/10 shadow-lg shadow-blue-500/20"
            onSelect={(card) => {
              setSelectedLegendId(card.id);
              setSelectedChampionId(null);
              setDeckEntries([]);
              setBattlefieldIds([]);
              setSideboardEntries([]);
              setRightPanel("main");
            }}
          />
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              disabled={!selectedLegendId}
              onClick={() => setStep("champion")}
              className="rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-bold disabled:opacity-40"
            >
              Continuer
            </button>
          </div>
        </section>
      )}

      {step === "champion" && selectedLegend && (
        <section>
          <h2 className="mb-2 text-2xl font-bold">Chosen champion</h2>
          <p className="mb-4 text-sm text-gray-500">
            Units Champion de <span className="text-white">{championIdentity(selectedLegend.name)}</span>
            {" "}({championsForLegend.length}).
          </p>

          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-400">Nombre de copies</span>
            <div className="flex gap-1 rounded-xl bg-white/5 p-1">
              {([1, 2, 3] as ChampionQty[]).map((qty) => (
                <button
                  key={qty}
                  type="button"
                  onClick={() => changeChampionQty(qty)}
                  className={`rounded-lg px-4 py-2 text-sm font-bold transition ${
                    championQty === qty
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  {qty}
                </button>
              ))}
            </div>
            {selectedChampion && (
              <span className="text-sm text-purple-300">
                {championQty}× {selectedChampion.name}
              </span>
            )}
          </div>

          {championsForLegend.length === 0 ? (
            <div className="glass rounded-2xl border border-white/5 p-8 text-center text-gray-400">
              Aucun champion unit trouvé pour cette légende.
            </div>
          ) : (
            <PaginatedCardGrid
              cards={championsForLegend}
              selectedId={selectedChampionId}
              selectedClassName="border-purple-500/60 bg-purple-500/10 shadow-lg shadow-purple-500/20"
              onSelect={selectChampion}
            />
          )}
          <div className="mt-8 flex justify-between">
            <button type="button" onClick={() => setStep("legend")} className="text-sm text-gray-400 hover:text-white">
              ← Légende
            </button>
            <button
              type="button"
              disabled={!selectedChampionId}
              onClick={() => setStep("cards")}
              className="rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-bold disabled:opacity-40"
            >
              Continuer
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
